/**
 * Stormforge Mode Hook
 * Phase 8: Easter egg activation via keyboard sequence
 * 
 * Activation: Type "STORMFORGE" anywhere in the app
 * Deactivation: Press Escape or type sequence again
 */

import { useEffect, useCallback, useRef } from 'react';
import { useMarketingMode } from '@/contexts/MarketingModeContext';
import { toast } from 'sonner';

const ACTIVATION_SEQUENCE = 'STORMFORGE';
const SEQUENCE_TIMEOUT_MS = 3000;

export function useStormforgeMode() {
  const { isStormforgeMode, toggleStormforgeMode } = useMarketingMode();
  const inputBufferRef = useRef('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetBuffer = useCallback(() => {
    inputBufferRef.current = '';
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Escape to deactivate
    if (event.key === 'Escape' && isStormforgeMode) {
      toggleStormforgeMode();
      toast.info('⚡ Stormforge Mode deactivated');
      return;
    }

    // Ignore if user is typing in an input/textarea
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Only track alphabetic keys
    if (event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
      // Reset timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Add to buffer
      inputBufferRef.current += event.key.toUpperCase();

      // Check if sequence matches
      if (inputBufferRef.current === ACTIVATION_SEQUENCE) {
        toggleStormforgeMode();
        if (!isStormforgeMode) {
          toast.success('⚡ STORMFORGE MODE ACTIVATED ⚡', {
            description: 'The forge burns with lightning!',
            duration: 3000,
          });
        } else {
          toast.info('⚡ Stormforge Mode deactivated');
        }
        resetBuffer();
        return;
      }

      // Check if current buffer could still match
      if (!ACTIVATION_SEQUENCE.startsWith(inputBufferRef.current)) {
        resetBuffer();
        return;
      }

      // Set timeout to reset buffer
      timeoutRef.current = setTimeout(resetBuffer, SEQUENCE_TIMEOUT_MS);
    }
  }, [isStormforgeMode, toggleStormforgeMode, resetBuffer]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyDown]);

  return { isStormforgeMode };
}
